/**
 * Board Health Store (Singleton)
 *
 * Centralised state shared between:
 *   - useBoardHealthProbe  (writer)
 *   - useBoardSelfHealing  (writer)
 *   - BoardErrorBoundary    (writer)
 *   - sendHeartbeat()       (reader)
 *
 * All timestamps are epoch-ms; strings are short for telemetry.
 */

// ── Recovery action enum ────────────────────────────────────────────────────
export const RecoveryAction = {
  NONE:              'NONE',
  REFRESH_DATA:      'REFRESH_DATA',
  RESUBSCRIBE_SOCKET:'RESUBSCRIBE_SOCKET',
  REMOUNT:           'REMOUNT',
  STABLE_FALLBACK:   'STABLE_FALLBACK',
  ROUTE_RELOAD:      'ROUTE_RELOAD',
};

// ── Blank-reason enum (board-specific, extends flightDataMonitor) ──────────
export const BoardBlankReason = {
  HEALTHY:          'HEALTHY',
  NO_CONTAINER:     'NO_CONTAINER',
  ZERO_SIZE:        'ZERO_SIZE',
  CSS_HIDDEN:       'CSS_HIDDEN',
  ZERO_ROWS:        'ZERO_ROWS',
  EMPTY_SCHEDULE:   'EMPTY_SCHEDULE',   // legitimate 0 flights
  FALLBACK_LATCHED: 'FALLBACK_LATCHED', // showFallback stuck while data exists
  ERROR_BOUNDARY:   'ERROR_BOUNDARY',
  UNKNOWN:          'UNKNOWN',
};

// ── Rolling-window counter helper ───────────────────────────────────────────
function rollingCount(timestamps, windowMs) {
  const cutoff = Date.now() - windowMs;
  // mutate in-place to keep array bounded
  while (timestamps.length > 0 && timestamps[0] < cutoff) timestamps.shift();
  return timestamps.length;
}

// ── Singleton state ─────────────────────────────────────────────────────────
const _state = {
  // Written by useBoardHealthProbe
  boardRenderOk:        true,
  boardRenderedAt:       null,     // epoch-ms of last confirmed healthy render
  boardVisibleRowCount:  0,
  boardContainerRect:    { w: 0, h: 0 },
  boardBlankReason:      BoardBlankReason.HEALTHY,

  // Written by useBoardSelfHealing
  boardLastRecoveryAction: RecoveryAction.NONE,
  lastRecoveryAt:        null,     // epoch-ms
  stableFallbackModeUntil: null,   // epoch-ms when stable-fallback expires (null = not active)

  // Rolling timestamp arrays for counting
  _recoveryTimestamps1h:  [],      // epoch-ms of each recovery attempt
  _recoveryTimestamps24h: [],
  _crashTimestamps5m:     [],      // from BoardErrorBoundary
  _crashTimestamps24h:    [],

  // Short string for last error
  boardLastErrorShort:    '',
};

// ── Public API ──────────────────────────────────────────────────────────────

/** Update probe results (called by useBoardHealthProbe every check) */
export function updateProbeResults({ renderOk, visibleRowCount, containerRect, blankReason }) {
  _state.boardRenderOk       = renderOk;
  _state.boardVisibleRowCount = visibleRowCount;
  _state.boardContainerRect  = containerRect || { w: 0, h: 0 };
  _state.boardBlankReason    = blankReason;
  if (renderOk) {
    _state.boardRenderedAt = Date.now();
  }
}

/** Record a recovery attempt */
export function recordRecoveryAttempt(action) {
  const now = Date.now();
  _state.boardLastRecoveryAction = action;
  _state.lastRecoveryAt = now;
  _state._recoveryTimestamps1h.push(now);
  _state._recoveryTimestamps24h.push(now);
}

/** Record an error-boundary crash */
export function recordBoardCrash(errorShort) {
  const now = Date.now();
  _state._crashTimestamps5m.push(now);
  _state._crashTimestamps24h.push(now);
  _state.boardLastErrorShort = String(errorShort || '').slice(0, 200);
}

/** Enter / exit stable-fallback mode */
export function setStableFallbackUntil(epochMs) {
  _state.stableFallbackModeUntil = epochMs;
}

/** Set last error string (from any source) */
export function setLastError(msg) {
  _state.boardLastErrorShort = String(msg || '').slice(0, 200);
}

// ── Getters for heartbeat / telemetry ───────────────────────────────────────

export function getBoardHealthForHeartbeat() {
  return {
    boardRenderOk:          _state.boardRenderOk,
    boardRenderedAt:        _state.boardRenderedAt ? new Date(_state.boardRenderedAt).toISOString() : null,
    boardVisibleRowCount:   _state.boardVisibleRowCount,
    boardContainerRect:     _state.boardContainerRect,
    boardBlankReason:       _state.boardBlankReason,
    boardLastRecoveryAction: _state.boardLastRecoveryAction,
    boardRecoveryCount1h:   rollingCount(_state._recoveryTimestamps1h, 60 * 60 * 1000),
    boardRecoveryCount24h:  rollingCount(_state._recoveryTimestamps24h, 24 * 60 * 60 * 1000),
    boardCrashCount5m:      rollingCount(_state._crashTimestamps5m, 5 * 60 * 1000),
    boardCrashCount24h:     rollingCount(_state._crashTimestamps24h, 24 * 60 * 60 * 1000),
    stableFallbackActive:   _state.stableFallbackModeUntil != null && Date.now() < _state.stableFallbackModeUntil,
    boardLastErrorShort:    _state.boardLastErrorShort,
  };
}

export function getRecoveryCount1h() {
  return rollingCount(_state._recoveryTimestamps1h, 60 * 60 * 1000);
}

export function getLastRecoveryAt() {
  return _state.lastRecoveryAt;
}

export function isInStableFallback() {
  return _state.stableFallbackModeUntil != null && Date.now() < _state.stableFallbackModeUntil;
}
